import { api } from "./api";

/**
 * Web Push helpers used by the "Push notifications" toggle in
 * `/app/notifications`. Everything here degrades gracefully:
 *   • No SW / no PushManager → `isSupported()` returns false, UI hides toggle.
 *   • VAPID unavailable server-side → subscribe() throws early.
 *   • Permission denied → we surface the error to the caller.
 */

export function isSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/** Convert a URL-safe base64 VAPID public key to the Uint8Array Push wants. */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i);
  return output;
}

async function waitForRegistration() {
  const reg =
    (await navigator.serviceWorker.getRegistration()) ||
    (await navigator.serviceWorker.ready);
  if (!reg) throw new Error("Service worker not registered yet");
  return reg;
}

export async function getCurrentSubscription() {
  if (!isSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return null;
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

export async function fetchVapidPublicKey() {
  const { data } = await api.get("/push/vapid-public-key");
  if (!data.enabled || !data.key) {
    throw new Error("Push not enabled on server");
  }
  return data.key;
}

export async function subscribe() {
  if (!isSupported()) throw new Error("Bu tarayıcı push bildirimleri desteklemiyor.");
  if (Notification.permission === "denied") {
    throw new Error(
      "Tarayıcı bu site için bildirimleri engellemiş. Adres çubuğundaki kilit/ayar simgesine tıklayıp Bildirimler'i 'İzin ver' yapın, sonra tekrar deneyin.",
    );
  }
  const perm = await Notification.requestPermission();
  if (perm === "denied") {
    throw new Error(
      "Bildirimler engellendi. Adres çubuğundaki kilit/ayar simgesinden Bildirimler'i 'İzin ver' yaparak açabilirsiniz.",
    );
  }
  if (perm !== "granted") {
    throw new Error("İzin penceresi kapatıldı — bildirimleri açmak için tekrar deneyin.");
  }
  const key = await fetchVapidPublicKey();
  const reg = await waitForRegistration();
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(key),
  });

  // Send subscription to backend. `sub.toJSON()` yields { endpoint, keys }.
  const json = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: json.endpoint,
    keys: json.keys,
    user_agent: navigator.userAgent,
    language: navigator.language,
  });
  return sub;
}

export async function unsubscribe() {
  const sub = await getCurrentSubscription();
  if (!sub) return { ok: true };
  const endpoint = sub.endpoint;
  try {
    await sub.unsubscribe();
  } catch {
    /* browser may already have unsubscribed */
  }
  try {
    await api.delete(`/push/subscribe?endpoint=${encodeURIComponent(endpoint)}`);
  } catch {
    /* ignore server errors — subscription is already dead client-side */
  }
  return { ok: true };
}

export async function sendTestPush() {
  const { data } = await api.post("/push/test", {});
  return data;
}

export async function fetchPushDiagnostics() {
  const { data } = await api.get("/push/diagnostics");
  return data;
}

/**
 * Nuke every subscription (server-side + browser) and re-subscribe with the
 * current server VAPID public key. Fixes the "server rotated keys but
 * browser is holding a stale subscription" state — which surfaces as
 * ``Could not deserialize key data`` on the server side because signatures
 * no longer match the browser's push service.
 */
export async function resetAndResubscribe() {
  // 1) Browser unsubscribe (all)
  if (isSupported()) {
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = await reg?.pushManager?.getSubscription();
      if (sub) {
        try {
          await sub.unsubscribe();
        } catch {
          /* browser may already have unsubscribed */
        }
      }
    } catch {
      /* ignore — best effort */
    }
  }
  // 2) Server-side wipe
  try {
    await api.post("/push/reset", {});
  } catch {
    /* ignore — old subs may already be gone */
  }
  // 3) Fresh subscribe with current VAPID pub key
  return subscribe();
}
